import React, { useState } from 'react';
import { authAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const ChangePassword = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [hoveredCancel, setHoveredCancel] = useState(false);
    const [hoveredSubmit, setHoveredSubmit] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
        if (errors[id]) {
            setErrors({ ...errors, [id]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        if (formData.new_password !== formData.new_password_confirmation) {
            setErrors({ new_password_confirmation: 'Passwords do not match' });
            toast.error('New passwords do not match', {
                position: "top-right",
                autoClose: 4000,
                theme: "colored"
            });
            setLoading(false);
            return;
        }

        if (formData.new_password.length < 6) {
            setErrors({ new_password: 'Password must be at least 6 characters' });
            toast.error('Password must be at least 6 characters', {
                position: "top-right",
                autoClose: 4000,
                theme: "colored"
            });
            setLoading(false);
            return;
        }

        try {
            const response = await authAPI.changePassword({
                current_password: formData.current_password,
                new_password: formData.new_password,
                new_password_confirmation: formData.new_password_confirmation
            });

            if (response.data.success) {
                toast.success('Password changed successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });

                setFormData({
                    current_password: '',
                    new_password: '',
                    new_password_confirmation: ''
                });

                setTimeout(() => {
                    if (window.confirm('Password changed successfully. Do you want to login again?')) {
                        localStorage.clear();
                        navigate('/login');
                    }
                }, 1000);
            }
        } catch (error) {
            console.error('Password change error:', error);
            if (error.response?.status === 401) {
                const errorMessage = error.response?.data?.message || 'Current password is incorrect';
                setErrors({ current_password: errorMessage });
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 4000,
                    theme: "colored"
                });
            } else if (error.response?.status === 422 && error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                const firstError = Object.values(error.response.data.errors)[0];
                toast.error(firstError[0], {
                    position: "top-right",
                    autoClose: 4000,
                    theme: "colored"
                });
            } else {
                const errorMessage = error.response?.data?.message || 'Failed to change password';
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 4000,
                    theme: "colored"
                });
                if (errorMessage.toLowerCase().includes('current password') ||
                    errorMessage.toLowerCase().includes('incorrect')) {
                    setErrors({ current_password: errorMessage });
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleNewPassword = () => setShowNewPassword(!showNewPassword);
    const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    // ----- inline styles -----
    const mainContentStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        backgroundColor: '#f4f7fc',
        minHeight: '100vh',
    };

    const pageHeaderStyle = {
        background: 'white',
        padding: '20px 30px',
        borderBottom: '1px solid #e2e8f0',
        marginBottom: 0,
    };

    const pageTitleStyle = {
        fontSize: '1.3rem',
        fontWeight: 700,
        color: '#0b2b3b',
        margin: 0,
    };

    const pageSubtitleStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        marginTop: '5px',
    };

    const contentWrapperStyle = {
        padding: '25px 30px',
        maxWidth: '800px',
        margin: '0 auto',
    };

    const cardStyle = {
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
    };

    const cardHeaderStyle = {
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
    };

    const cardHeaderTitleStyle = {
        margin: 0,
        fontSize: '1rem',
        fontWeight: 600,
        color: '#0b2b3b',
    };

    const cardHeaderSubStyle = {
        fontSize: '0.8rem',
        color: '#64748b',
        margin: '4px 0 0',
    };

    const cardBodyStyle = {
        padding: '1.5rem',
    };

    const formGroupStyle = {
        marginBottom: '1rem',
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '0.25rem',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#1e293b',
    };

    const requiredAsteriskStyle = {
        content: '"*"',
        color: '#ef4444',
        marginLeft: '4px',
    };

    const inputGroupStyle = {
        display: 'flex',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        overflow: 'hidden',
        background: 'white',
        transition: 'border-color 0.2s, box-shadow 0.2s',
    };

    const inputGroupFocusStyle = {
        borderColor: '#2c7da0',
        boxShadow: '0 0 0 3px rgba(44,125,160,0.1)',
    };

    const inputGroupTextStyle = {
        padding: '0.5rem 0.75rem',
        background: '#f8fafc',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        color: '#64748b',
    };

    const inputStyle = (hasError) => ({
        flex: 1,
        padding: '0.5rem 0.75rem',
        border: 'none',
        outline: 'none',
        fontSize: '0.9rem',
        borderRadius: 0,
        background: 'white',
        ...(hasError && { border: '1px solid #ef4444' }),
    });

    const toggleButtonStyle = {
        padding: '0.5rem 0.75rem',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
    };

    const errorStyle = {
        color: '#ef4444',
        fontSize: '0.8rem',
        marginTop: '0.25rem',
    };

    const smallTextStyle = {
        fontSize: '0.7rem',
        color: '#64748b',
        marginTop: '0.25rem',
        display: 'block',
    };

    const requirementsStyle = {
        background: '#f8fafc',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem',
    };

    const requirementsTitleStyle = {
        fontSize: '0.8rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        color: '#0b2b3b',
    };

    const requirementsListStyle = {
        margin: 0,
        paddingLeft: '1.2rem',
    };

    const requirementItemStyle = {
        fontSize: '0.7rem',
        color: '#64748b',
        margin: '0.25rem 0',
    };

    const hrStyle = {
        margin: '1rem 0',
        border: 'none',
        borderTop: '1px solid #e2e8f0',
    };

    const buttonRowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1rem',
    };

    const cancelButtonStyle = (hovered) => ({
        padding: '10px 25px',
        background: hovered ? '#e2e8f0' : '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        fontSize: '0.85rem',
        fontWeight: 600,
        color: '#475569',
        cursor: 'pointer',
        transition: 'background 0.2s',
    });

    const submitButtonStyle = (hovered, disabled) => ({
        padding: '10px 25px',
        background: disabled ? '#94a3b8' : (hovered ? '#0a1c28' : '#0b2b3b'),
        border: 'none',
        borderRadius: '10px',
        fontSize: '0.85rem',
        fontWeight: 600,
        color: 'white',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        transform: hovered && !disabled ? 'translateY(-2px)' : 'none',
        boxShadow: hovered && !disabled ? '0 5px 15px rgba(11,43,59,0.3)' : 'none',
    });

    return (
        <div style={mainContentStyle}>
            <ToastContainer position="top-right" autoClose={3000} />

            <div style={pageHeaderStyle}>
                <h1 style={pageTitleStyle}>
                    <i className="fas fa-key" style={{ marginRight: '0.5rem' }}></i> Change Password
                </h1>
                <p style={pageSubtitleStyle}>Update your account password</p>
            </div>

            <div style={contentWrapperStyle}>
                <div style={cardStyle}>
                    <div style={cardHeaderStyle}>
                        <h5 style={cardHeaderTitleStyle}>
                            <i className="fas fa-lock" style={{ marginRight: '0.5rem' }}></i> Password Settings
                        </h5>
                        <p style={cardHeaderSubStyle}>Ensure your account is secure with a strong password</p>
                    </div>
                    <div style={cardBodyStyle}>
                        <form onSubmit={handleSubmit}>
                            {/* Current Password */}
                            <div style={formGroupStyle}>
                                <label style={labelStyle}>
                                    Current Password <span style={requiredAsteriskStyle}>*</span>
                                </label>
                                <div style={inputGroupStyle}>
                                    <span style={inputGroupTextStyle}>
                                        <i className="fas fa-lock"></i>
                                    </span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="current_password"
                                        style={inputStyle(!!errors.current_password)}
                                        value={formData.current_password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        style={toggleButtonStyle}
                                        onClick={togglePasswordVisibility}
                                    >
                                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                                {errors.current_password && (
                                    <div style={errorStyle}>{errors.current_password[0] || errors.current_password}</div>
                                )}
                            </div>

                            {/* New Password */}
                            <div style={formGroupStyle}>
                                <label style={labelStyle}>
                                    New Password <span style={requiredAsteriskStyle}>*</span>
                                </label>
                                <div style={inputGroupStyle}>
                                    <span style={inputGroupTextStyle}>
                                        <i className="fas fa-key"></i>
                                    </span>
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        id="new_password"
                                        style={inputStyle(!!errors.new_password)}
                                        value={formData.new_password}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        style={toggleButtonStyle}
                                        onClick={toggleNewPassword}
                                    >
                                        <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                                {errors.new_password && (
                                    <div style={errorStyle}>{errors.new_password[0] || errors.new_password}</div>
                                )}
                                <small style={smallTextStyle}>Password must be at least 6 characters</small>
                            </div>

                            {/* Confirm New Password */}
                            <div style={formGroupStyle}>
                                <label style={labelStyle}>
                                    Confirm New Password <span style={requiredAsteriskStyle}>*</span>
                                </label>
                                <div style={inputGroupStyle}>
                                    <span style={inputGroupTextStyle}>
                                        <i className="fas fa-check-circle"></i>
                                    </span>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="new_password_confirmation"
                                        style={inputStyle(!!errors.new_password_confirmation)}
                                        value={formData.new_password_confirmation}
                                        onChange={handleChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        style={toggleButtonStyle}
                                        onClick={toggleConfirmPassword}
                                    >
                                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                                {errors.new_password_confirmation && (
                                    <div style={errorStyle}>{errors.new_password_confirmation[0] || errors.new_password_confirmation}</div>
                                )}
                            </div>

                            {/* Password Requirements */}
                            <div style={requirementsStyle}>
                                <h6 style={requirementsTitleStyle}>Password Requirements:</h6>
                                <ul style={requirementsListStyle}>
                                    <li style={requirementItemStyle}>Minimum 6 characters</li>
                                    <li style={requirementItemStyle}>Use a mix of letters and numbers</li>
                                    <li style={requirementItemStyle}>Avoid common passwords</li>
                                </ul>
                            </div>

                            <hr style={hrStyle} />

                            <div style={buttonRowStyle}>
                                <button
                                    type="button"
                                    style={cancelButtonStyle(hoveredCancel)}
                                    onMouseEnter={() => setHoveredCancel(true)}
                                    onMouseLeave={() => setHoveredCancel(false)}
                                    onClick={() => navigate(-1)}
                                >
                                    <i className="fas fa-arrow-left" style={{ marginRight: '0.5rem' }}></i> Back
                                </button>
                                <button
                                    type="submit"
                                    style={submitButtonStyle(hoveredSubmit, loading)}
                                    onMouseEnter={() => setHoveredSubmit(true)}
                                    onMouseLeave={() => setHoveredSubmit(false)}
                                    disabled={loading}
                                >
                                    <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-save'}`} style={{ marginRight: '0.5rem' }}></i>
                                    {loading ? 'Changing...' : 'Change Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;